// Story 7.1 — /verify-email SPA route. Backend sends an email whose link
// points at `${APP_PUBLIC_BASE_URL}/verify-email?token=<>` (see
// server/src/services/auth_service.rs::verification_url). That URL lands
// HERE, on the SPA. We read `?token` from the URL, POST it back to the
// backend, then navigate to `/welcome` on success (Story 3.9's target
// via the backend's 302).
//
// Note the URL shape difference from the story spec: the backend URL is
// `/verify-email?token=<>` (query), NOT `/verify-email/:token` (path).
// The SPA route matches the backend-generated URL exactly.

// LEGAL CHROME: the two TERMINAL states (missing token, expired/used link)
// carry <SiteFooter>. Those are dead ends — the visitor is stuck outside
// the product, and the footer is where they find the operator's contact
// address to ask why. The third state is the transient "verifying…"
// spinner: it is aria-busy and redirects to /welcome within a request, so
// mounting a contentinfo landmark there would only flash chrome (and hand
// a screen reader a footer it is about to lose). Same rule the landing's
// session-check spinner follows.

import { useEffect, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { SiteFooter } from "@greenhold/components";

import { useVerifyEmail } from "../features/auth/useVerifyEmail";

/** Page shell for the two terminal states: centred card + legal footer. */
function VerifyEmailShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-brand-forest-900 text-brand-paper-100 flex flex-col">
      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded border border-brand-gold-500/40 bg-brand-forest-800 p-8 flex flex-col gap-4">
          {children}
        </div>
      </main>
      <SiteFooter className="mt-0" />
    </div>
  );
}

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const verify = useVerifyEmail();

  useEffect(() => {
    if (!token) return;
    verify.mutate(
      { token },
      {
        onSuccess: () => {
          navigate("/welcome", { replace: true });
        },
      },
    );
    // Only fire when the token is first available; ignore hook identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <VerifyEmailShell>
        <h1 className="font-heading text-2xl m-0 text-brand-gold-300">
          Missing verification token.
        </h1>
        <p className="font-body text-brand-paper-100/80 m-0 leading-relaxed">
          This link is incomplete.{" "}
          <Link to="/" className="underline hover:text-brand-gold-300">
            Return home
          </Link>
          .
        </p>
      </VerifyEmailShell>
    );
  }

  if (verify.isError) {
    return (
      <VerifyEmailShell>
        <h1 className="font-heading text-2xl m-0 text-brand-gold-300">
          This link has expired or been used.
        </h1>
        <p className="font-body text-brand-paper-100/80 m-0 leading-relaxed">
          Please{" "}
          <Link to="/" className="underline hover:text-brand-gold-300">
            sign in
          </Link>{" "}
          to request a fresh verification.
        </p>
      </VerifyEmailShell>
    );
  }

  return (
    <main
      className="min-h-screen bg-brand-forest-900 text-brand-paper-100 flex items-center justify-center px-6"
      aria-busy="true"
    >
      <div className="w-full max-w-md rounded border border-brand-gold-500/40 bg-brand-forest-800 p-8 flex flex-col items-center gap-4">
        <div
          className="h-10 w-10 rounded-full border-2 border-brand-gold-300 border-t-transparent animate-spin"
          aria-label="Verifying"
        />
        <p className="font-body text-brand-paper-100/80 m-0">
          Verifying your email…
        </p>
      </div>
    </main>
  );
}
